import { accountEndpoints } from '@pff-consumer/api-sdk'
import {
  AccountsResponse,
  CheckoutResponse,
  CheckoutResponseError,
  SubscribeCompleteSuccessResponse,
  SubscribeCompleteBody,
  SubscribeCompleteErrorResponse,
} from '@pff-consumer/schema'
import { consumerApi } from '../consumer-api/consumer-api'

// Define a service using a base URL and expected endpoints
export const accountApi = consumerApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getUser: builder.query({
      query: () => {
        return {
          url: accountEndpoints.user().url,
          method: 'get',
          headers: {
            'Content-type': 'application/json',
          },
        }
      },
      keepUnusedDataFor: 0,
    }),
    deleteUser: builder.query({
      query: () => {
        return {
          url: accountEndpoints.user().url,
          method: 'delete',
          headers: {
            'Content-type': 'application/json',
          },
        }
      },
    }),
    signIn: builder.mutation<AccountsResponse, { email: string; password: string }>({
      query: (params) => {
        return {
          url: accountEndpoints.signIn().url,
          method: 'post',
          body: {
            email: params.email,
            password: params.password,
          },
          headers: {
            'Content-type': 'application/json',
          },
        }
      },
    }),
    createAccount: builder.mutation<AccountsResponse, { email: string; password: string }>({
      query: (params) => ({
        url: accountEndpoints.createAccount().url,
        method: 'post',
        body: {
          email: params.email,
          password: params.password,
        },
        headers: {
          'Content-type': 'application/json',
        },
      }),
    }),
    getAppStatus: builder.query<AccountsResponse, { uid: string }>({
      query: (params) => {
        return {
          url: accountEndpoints.appUser({ uid: params.uid }).url,
          method: 'get',
          headers: {
            'Content-type': 'application/json',
          },
        }
      },
    }),
    signinWithApple: builder.mutation<
      AccountsResponse,
      {
        idToken: string
        firstName: string
        lastName: string
        nonce: string
        clientId: string
      }
    >({
      query: (params) => {
        return {
          url: accountEndpoints.apple().url,
          method: 'post',
          body: {
            id_token: params.idToken,
            first_name: params.firstName,
            last_name: params.lastName,
            nonce: params.nonce,
            client_id: params.clientId,
          },
          headers: {
            'Content-type': 'application/json',
          },
        }
      },
    }),
    upsertDevice: builder.mutation<any, { deviceId: string; systemName: string }>({
      query: (params) => {
        return {
          url: accountEndpoints.device().url,
          method: 'post',
          body: {
            deviceId: params.deviceId,
            systemName: params.systemName,
          },
          headers: {
            'Content-type': 'application/json',
          },
        }
      },
    }),
    answerSurvey: builder.mutation<
      any,
      { surveyId: string; deviceId: string; answers: { questionId: number; answerId: number | null }[] }
    >({
      query: (params) => {
        return {
          url: accountEndpoints.answerSurvey({ surveyId: params.surveyId }).url,
          method: 'post',
          body: {
            deviceId: params.deviceId,
            answers: params.answers,
          },
          headers: {
            'Content-type': 'application/json',
          },
        }
      },
    }),
    activateSubscription: builder.mutation<
      AccountsResponse,
      {
        grootUid: string
        productId: string
        startDate: number
        expiryDate: number
        transactionId: string
      }
    >({
      query: (params) => {
        return {
          url: accountEndpoints.activateSubscription().url,
          method: 'post',
          body: {
            groot_uid: params.grootUid,
            product_id: params.productId,
            start_date: params.startDate,
            expiry_date: params.expiryDate,
            original_transaction_id: params.transactionId,
          },
        }
      },
    }),
    checkout: builder.mutation<
      CheckoutResponse | CheckoutResponseError,
      {
        planCode: string
        email: string
        recurlyToken: string
        recaptchaToken: string
        promoCode?: string
        paymentType?: string
      }
    >({
      query: (params) => {
        return {
          url: accountEndpoints.checkout().url,
          method: 'post',
          body: {
            planCode: params.planCode,
            email: params.email,
            recurlyToken: params.recurlyToken,
            recaptchaToken: params.recaptchaToken,
            promoCode: params.promoCode,
            paymentType: params.paymentType,
          },
        }
      },
    }),
    complete: builder.mutation<
      SubscribeCompleteSuccessResponse | SubscribeCompleteErrorResponse,
      SubscribeCompleteBody
    >({
      query: (body) => {
        return {
          url: accountEndpoints.complete().url,
          method: 'post',
          body,
        }
      },
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetUserQuery,
  useSignInMutation,
  useCreateAccountMutation,
  useGetAppStatusQuery,
  useLazyGetAppStatusQuery,
  useLazyGetUserQuery,
  useDeleteUserQuery,
  useLazyDeleteUserQuery,
  useSigninWithAppleMutation,
  useUpsertDeviceMutation,
  useAnswerSurveyMutation,
  useActivateSubscriptionMutation,
  useCheckoutMutation,
  useCompleteMutation,
} = accountApi

// Export getters to be used when rendering components on the server
export const { getUser, signIn, createAccount, getAppStatus, signinWithApple, activateSubscription } =
  accountApi.endpoints
